<template>
  <listing-layout>
    <div slot="heading">
      <h1 class="h2 mb-3">{{ publishing ? 'Select Events to Publish' : draftDetails.name + ' Events' }}</h1>
      <div class="mb-3" v-if="!publishing" style="color: #000;">
        <div><strong>Draft ID:</strong> {{ draftId }}</div>
        <div v-if="draftDetails.created_at"><strong>Created:</strong> {{ formatDateTime(draftDetails.created_at) }}</div>
        <div v-if="draftDetails.updated_at"><strong>Updated:</strong> {{ formatDateTime(draftDetails.updated_at) }}</div>
      </div>
      <div class="d-md-flex d-block flex-wrap ms-auto justify-content-end mb-2">
        <button @click="exportEventMembers(draftDetails.id)" class="btn btn-primary px-xl-5 mt-2 mt-sm-0 mr-md-2">
          Export
          <span class="d-none d-xl-inline"> Casual Members</span>
          <span class="d-inline d-xl-none"> CM</span>
        </button>
        <router-link v-if="options.addEvent"
          :to="{ name: 'Create Event', params: { draftId: String(draftId) } }"
          class="btn btn-primary px-xl-5 mt-2 mt-sm-0">
          Add
          <span class="d-none d-xl-inline"> Event</span>
        </router-link>
      </div>
    </div>

    <div slot="content">
      <div class="alert alert-success" v-if="successMessage">{{ successMessage }}</div>
      <div class="alert alert-info" v-if="status">{{ status }}</div>
      <div class="alert alert-warning" v-if="error">{{ error }}</div>
      <div class="alert alert-info" v-if="changeStatuses.filter(i => i.changed).length">
        The following statuses were updated after publishing:
        <ul class="mb-0 mt-1">
          <li v-for="(item, i) in changeStatuses.filter(i => i.changed)" :key="i">
            {{ item.type === 'horse' ? 'Horse' : 'Member' }} <strong>{{ item.name }}</strong> ({{ item.event_name }}): status {{ item.old_status_name }} → {{ item.new_status_name }}
          </li>
        </ul>
      </div>
      <div class="alert alert-warning" v-if="warningItems.length">
        There are issues with the following events:
        <ul class="mb-0 mt-1">
          <li v-for="(item, i) in warningItems" :key="i">{{ item.name }}: <strong>{{ item.reason }}</strong></li>
        </ul>
      </div>

      <strong v-if="draftDetails.is_final">This draft is marked as the National Finals</strong>

      <data-table :dataRows="rows" :dataColumns="columns" :options="options">
        <tableSelectRowTemplate slot-scope="{ row }" slot="expandedSelect" :row="row" class="z-1 p-3"
          v-if="publishing" />
        <template slot="expandedHeader" slot-scope="{ row }">
          <span class="d-inline-flex align-items-center flex-wrap flex-grow-1 pl-2" style="gap: 4px">
            <router-link
              :to="{ name: 'Create Event', params: { draftId: String(draftId), eventId: String(row.id) } }"
              class="btn btn-link text-wrap text-left p-0 btn-expand-sm"
            >{{ row.name }}</router-link>
            <span
              v-if="row.status"
              class="badge"
              :class="row.status === 'Finalised' ? 'alert-success' : row.status === 'Pending' ? 'alert-info' : 'alert-warning'"
              style="min-width: 0"
            >{{ row.status }}</span>
          </span>
        </template>
        <div slot-scope="{ row }" slot="expandedRow">
          <ul class="list-unstyled mb-0 pt-2">
            <li class="pt-3 d-block">
              <router-link
                :to="{ name: 'Create Event', params: { draftId: String(draftId), eventId: String(row.id) } }"
                class="btn btn-sm btn-primary mb-2 mb-lg-0 px-lg-3 px-xl-4"
              >Edit</router-link>
              <button
                :class="['btn btn-sm btn-block-mob mb-2 mb-lg-0', processing ? 'btn-primary-dark' : 'btn-primary']"
                @click.prevent="removeModal(row.id)">
                {{ !processing ? 'Remove' : '' }}
                <div v-if="processing" class="spinner-border text-light" role="status"><span
                    class="sr-only">Loading...</span></div>
              </button>
              <modal v-if="showModal" @close="showModal = false">
                <div slot="body">
                  <div class="alert alert-warning text-center">
                    You are about to remove the event ‘{{ row.name }}’. Are you sure?
                  </div>
                  <div class="d-flex justify-content-center">
                    <button class="btn btn-outline-primary" @click.prevent="showModal = false">Cancel</button>
                    <button class="btn btn-primary ml-2" @click.prevent="remove(); showModal = false">Remove</button>
                  </div>
                </div>
              </modal>
            </li>
          </ul>
        </div>
      </data-table>
      <div class="sticky-bottom alert bg-primary text-white d-flex flex-wrap align-items-center mb-0" role="alert"
        v-if="publishing">
        <div class="flex-grow-1 pr-3 text-center text-lg-left" v-if="$store.getters.selectedRows.length">
          <strong>You have selected: </strong>
          <span v-for="(row, i) in $store.getters.selectedRows" :key="i">
            {{ row.name + (i == $store.getters.selectedRows.length - 1 ? '' : ',') }}
          </span>
        </div>
        <button v-if="$store.getters.selectedRows.length"
          :class="['btn btn-block-mob mt-2 mt-lg-0 text-nowrap', processing2 ? 'btn-primary-dark' : 'btn-outline-white']"
          @click="publishEvents">
          {{ !processing2 ? 'Publish Events' : '' }}
          <div v-if="processing2" class="spinner-border text-light" role="status"><span
              class="sr-only">Loading...</span></div>
        </button>
        <button
          :class="['btn btn-outline-white btn-block-mob', $store.getters.selectedRows.length ? 'ml-lg-2' : 'ml-lg-auto']"
          @click.prevent="publishing = !publishing">Cancel</button>
      </div>
      <div class="d-lg-flex justify-content-between align-items-end mt-3" v-if="!publishing && rows.length">
        <p class="mb-lg-0">Now that you have added your event, you can choose to publish the points associated with it.
          <br class="d-none d-xl-block">This will publicly display the event results to the ACA public website.
        </p>
        <button class="btn btn-block-mob btn-primary flex-md-shrink-0 mb-2 mb-lg-0 ml-lg-4"
          @click.prevent="publishing = !publishing">Publish Points</button>
      </div>
    </div>
  </listing-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
* to their respective fields in the ListingLayout component. */

import axios from 'axios'
import listingLayout from '@/components/ListingLayout'
import dataTable from '@/components/DataTable'
import tableTextTemplate from '@/components/DataTableFields/Text'
import tableButtonTemplate from '@/components/DataTableFields/Button'
import tableSelectRowTemplate from '@/components/DataTableFields/SelectRow'
import modal from '@/components/Modal'
import eventBus from '@/store/EventBus.js'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import { formatDateTime } from '@/utils/date'

export default {
  name: 'EventsList',
  mixins: [LEGACYBROWSERS],
  props: ['details', 'success', 'placeholder'],
  components: {
    'listing-layout': listingLayout,
    'data-table': dataTable,
    tableTextTemplate,
    tableButtonTemplate,
    tableSelectRowTemplate,
    modal
  },
  data() {
    return {
      draftId: this.$route.params.draftId,
      draftDetails: this.details || this.$store.state.draft || {},
      successMessage: this.success || '',
      status: '',
      error: '',
      warningItems: [],
      changeStatuses: [],
      errorStatus: '',
      rows: [],
      showModal: false,
      processing: false,
      processing2: false,
      publishing: false,
      columns: [
        {
          label: 'Event',
          headerClass: 'd-none d-lg-table-cell',
          sortable: true,
          filterable: true,
          visible: true,
          component: ({
            props: ['row'],
            computed: {
              eventTo() {
                return { name: 'Create Event', params: { draftId: String(this.$route.params.draftId || this.row.draft_id), eventId: String(this.row.id) } }
              },
              statusClass() {
                return this.row.status === 'Finalised' ? 'alert-success' : this.row.status === 'Pending' ? 'alert-info' : 'alert-warning'
              }
            },
            // Real <router-link> (an <a href>), not the legacy `<router-link tag="button">`,
            // so the event opens with middle-click / "open in new tab". EventAdd loads by :eventId.
            template: `<span class="d-inline-flex align-items-center flex-wrap" style="gap: 4px"><router-link :to="eventTo" class="btn btn-link text-wrap text-left p-0">{{ row.name }}</router-link><span v-if="row.status" class="badge" :class="statusClass" style="min-width: 0">{{ row.status }}</span></span>`
          }),
          representedAs(row) { return `${row.name}` }
        },
        {
          label: 'Rounds',
          field: '',
          align: 'left',
          sortable: false,
          filterable: false,
          visible: true,
          headerClass: 'd-none d-lg-table-cell column-lg-medium',
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.rounds === 0 ? '-' : String(row.rounds)" :row="row" />`
          })
        },
        {
          label: 'Event Type',
          field: '',
          align: 'left',
          sortable: false,
          filterable: false,
          visible: true,
          headerClass: 'd-none d-lg-table-cell column-lg-medium',
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.typeName" :row="row" />`
          })
        },
        {
          label: 'Edit',
          field: '',
          align: 'left',
          sortable: false,
          filterable: false,
          headerClass: 'd-none d-lg-table-cell column-lg-medium',
          visible: true,
          component: ({
            props: ['row'],
            computed: {
              eventTo() {
                return { name: 'Create Event', params: { draftId: String(this.$route.params.draftId || this.row.draft_id), eventId: String(this.row.id) } }
              }
            },
            template: `<router-link :to="eventTo" class="btn btn-sm btn-primary mb-2 mb-lg-0 px-lg-3 px-xl-4">Edit</router-link>`
          })
        },
        {
          label: 'Remove',
          field: '',
          align: 'left',
          sortable: false,
          filterable: false,
          headerClass: 'd-none d-lg-table-cell column-lg-medium',
          visible: true,
          component: ({
            props: ['row'],
            components: { modal, tableButtonTemplate },
            data() {
              return {
                showModal: false,
                processing: false
              }
            },
            methods: {
              removeModal() {
                this.showModal = true
              },
              remove() {
                this.processing = true
                axios.post(`${this.$api}/admin/event-delete`, {
                  id: this.row.id
                }).then((response) => {
                  this.processing = false
                  if (response.data.status === 'success') {
                    this.successMessage = 'The event has been removed.'
                    eventBus.$emit('removed')
                  }
                }).catch((error) => {
                  // eslint-disable-next-line
                  console.log(error)
                  this.successMessage = ''
                  this.error = 'Unable to receive data from the server. Please reload the page.'
                })
              }
            },
            template: `
              <div>
                <button :class="['btn btn-sm mb-2 mb-lg-0', processing ? 'btn-primary-dark' : 'btn-primary']" @click.prevent="removeModal">
                  {{ !processing ? 'Remove' : '' }}
                  <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
                </button>
                <modal v-if="showModal" @close="showModal = false">
                  <div slot="body">
                    <div class="alert alert-warning text-center">
                      Removing an event will cause associated points and scores to also be removed.<br>
                      You are about to remove the event ‘{{row.name}}’. Are you sure?
                    </div>
                    <div class="d-flex justify-content-center">
                      <button class="btn btn-outline-primary" @click.prevent="showModal = false">Cancel</button>
                      <button class="btn btn-primary ml-2" @click.prevent="remove();showModal = false">Confirm</button>
                    </div>
                  </div>
                </modal>
              </div>`
          })
        }
      ],
      options: {
        loaded: false,
        addEvent: true,
        theDraft: this.details ? this.details.id : Object.entries(this.$store.state.draft).length ? this.$store.state.draft.id : null,
        theDraftFinal: this.details ? this.details.is_final : Object.entries(this.$store.state.draft).length ? this.$store.state.draft.is_final : false
      }
    }
  },
  mounted() {
    if (this.details) {
      // Instant placeholder render while the real draft details load below.
      this.draftDetails = this.details
      this.$store.commit('updateDraft', this.details)
      this.$store.commit('updatePlaceholder', this.details.name)
    }
    if (this.draftId) {
      axios.get(`${this.$api}/admin/draft-details/${this.draftId}`).then((response) => {
        if (response.data.status === 'success') {
          this.draftDetails = response.data.data
          this.$store.commit('updateDraft', this.draftDetails)
          this.$store.commit('updatePlaceholder', this.draftDetails.name)
          this.getEvents()
        } else {
          this.$router.push({ name: 'Drafts' })
        }
      }).catch(() => {
        this.$router.push({ name: 'Drafts' })
      })
    } else {
      this.$router.push({ name: 'Drafts' })
    }
  },
  methods: {
    formatDateTime,
    removeModal(theRowID) {
      this.rowID = theRowID
      this.showModal = true
    },
    getEvents() {
      axios.get(`${this.$api}/admin/event-list/${this.draftId}`).then((response) => {
        // eslint-disable-next-line
        // console.log(response)
        if (response.data.status === 'success') {
          this.error = ''
          const theRows = response.data.data
          theRows.forEach((row) => {
            row.draftName = this.draftDetails.name
            row.typeName = row.type ? row.type.name : '-'
          })
          this.rows = theRows
          this.options.loaded = true
        } else {
          this.error = 'Unable to receive data from the server. Please reload the page.'
        }
      })
    },
    remove() {
      this.processing = true
      axios.post(`${this.$api}/admin/event-delete`, {
        id: this.rowID
      }).then((response) => {
        this.processing = false
        if (response.data.status === 'success') {
          this.successMessage = 'The payment has been removed.'
          this.error = ''
          eventBus.$emit('removed')
        }
      }).catch((error) => {
        this.processing = false
        // eslint-disable-next-line
        console.log(error)
        this.successMessage = ''
        this.error = 'Unable to receive data from the server. Please reload the page.'
      })
    },
    exportEventMembers(draft_id) {
      this.processing = true
      axios.get(`${this.$api}/admin/export/draft/${draft_id}/event-members`)
        .then((response) => {
          this.processing = false

          if (response.data.status === 'success') {
            const fileName = 'Casual Members'

            if (window.navigator.msSaveOrOpenBlob) {
              const blobObject = new Blob([response.data.results])
              window.navigator.msSaveOrOpenBlob(blobObject, `${fileName} - ${this.draftDetails.name}.csv`)
            } else if (this.isIOS()) {
              const reader = new FileReader()
              reader.fileName = `${fileName}.csv`
              const out = new Blob([response.data.results], { type: 'data:text/csv;charset=utf-8' })

              reader.onload = () => {
                window.location.href = reader.result
              }
              reader.readAsDataURL(out)
            } else {
              const url = window.URL ? window.URL.createObjectURL(new Blob([response.data.results], { type: 'data:text/csv;charset=utf-8' })) : window.webkitURL.createObjectURL(new Blob([response.data.results], { type: 'data:text/csv;charset=utf-8' }))
              const link = document.createElement('a')
              link.href = url
              link.setAttribute('download', `${fileName} - ${this.draftDetails.name}.csv`)
              document.body.appendChild(link)
              link.click()
              setTimeout(() => {
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
              }, 0)
            }
          } else if (response.data.status === 'error') {
            this.error = response.data.message
          }
        }).catch((error) => {
          this.processing = false
          console.log(error)
        })
    },
    publishEvents() {
      if (this.draftDetails?.zone_id === null) {
        // eslint-disable-next-line no-alert
        alert('Please fill in the zone field in the draft')
        return
      }
      this.processing2 = true
      axios.post(`${this.$api}/admin/event-publish`, {
        eventIds: this.$store.getters.selectedRows.map(select => select.id)
      }).then((response) => {
        this.processing2 = false
        this.publishing = false
        // eslint-disable-next-line
        // console.log(response)
        if (response.data.status === 'success') {
          this.successMessage = 'The events have been successfully published.'
          this.warningItems = response.data.data?.failedEvents?.length > 0 ? response.data.data.failedEvents : []
          this.changeStatuses = response.data.data?.changeStatuses?.length > 0 ? response.data.data.changeStatuses : []
          this.rows = []
          this.options.loaded = false
          this.getEvents()
          eventBus.$emit('deselect-all')
        }
      }).catch((error) => {
        this.processing2 = false
        // eslint-disable-next-line
        console.log(error)
        this.successMessage = ''
        this.error = 'Unable to receive data from the server. Please reload the page.'
      })
    }
  },
  watch: {
    publishing() {
      /* Add checkbox field if publishing events */
      if (this.publishing && this.columns.filter(column => column.publishing === true).length === 0) {
        this.columns.unshift({
          label: '',
          field: '',
          headerClass: 'column-lg-small d-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableSelectRowTemplate },
            template: `<tableSelectRowTemplate :row="row" />`
          }),
          publishing: true
        })
        window.scrollTo(0, 0)
      } else {
        this.columns.shift()
        eventBus.$emit('deselect-all')
        this.$store.commit('deselectAllRows')
      }
    }
  },
  beforeDestroy() {
    eventBus.$emit('deselect-all')
    this.$store.commit('deselectAllRows')
  },
  created() {
    eventBus.$on('removed', () => {
      this.options.loaded = false
      this.rows = []
      this.getEvents()
    })
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Drafts/Events/Listing/Events.vue